/**
 * Ролик-файл в блоке `video_embed` (слово владельца 21.10.2026).
 *
 * Зеркало серверной правды `backend/src/ai-funnels/block-schema/video-file.ts`:
 * перечень заполнений, границы высоты коробки и поля вида. Совпадение в обе
 * стороны (и с панелью, и со словарём) держит сторож сервера
 * `video-file.ends.spec.ts`.
 *
 * Здесь же — распознавание адреса файла и зажим отрисовки: ручной сейв
 * редактора идёт мимо схемы сервера, поэтому негодное значение плеер не
 * рисует, а берёт значение по умолчанию.
 */
import type { EditorBlock } from '@/store/editorSlice';

/** Как ролик ложится в коробку: `cover` — заполнить с обрезкой, `contain` — целиком. */
export const VIDEO_FITS = ['cover', 'contain'] as const;
export type VideoFit = (typeof VIDEO_FITS)[number];

/** Числовые границы (высота коробки, px). */
export const VIDEO_FILE_BOUNDS = {
  videoBoxHeight: { min: 80, max: 800 },
} as const;

/** Расширения пути, по которым адрес считается файлом видео. */
export const VIDEO_FILE_EXTENSIONS = ['.mp4', '.webm', '.mov', '.m4v'] as const;

/** Поля вида ролика-файла — ровно те, что в схеме сервера. */
export type VideoFileSettings = Pick<
  EditorBlock,
  'videoLoop' | 'videoFit' | 'videoBoxHeight'
>;

/**
 * Адрес — файл видео: абсолютный http(s), путь (без `?…` и `#…`) оканчивается
 * на расширение видео. Прочие схемы (`javascript:`, `data:`) и адреса, где
 * расширение стоит только в параметрах, — не файл.
 */
export function isVideoFileUrl(url: string): boolean {
  if (!url) return false;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false;
  const path = parsed.pathname.toLowerCase();
  return VIDEO_FILE_EXTENSIONS.some((ext) => path.endsWith(ext));
}

export interface VideoFileLook {
  loop: boolean;
  fit: VideoFit;
  /** undefined — высота по пропорции ролика. */
  boxHeight: number | undefined;
}

/**
 * Зажим полей вида. Нет поля (или негодное) — по кругу при самозапуске,
 * целиком (`contain`), высота по пропорции ролика.
 */
export function readVideoFile(
  r: VideoFileSettings,
  autoplayOnScreen: boolean,
): VideoFileLook {
  const loop =
    typeof r.videoLoop === 'boolean' ? r.videoLoop : autoplayOnScreen;
  const fit = (VIDEO_FITS as readonly string[]).includes(r.videoFit as string)
    ? (r.videoFit as VideoFit)
    : 'contain';
  const { min, max } = VIDEO_FILE_BOUNDS.videoBoxHeight;
  const h = r.videoBoxHeight;
  const boxHeight =
    typeof h === 'number' && Number.isFinite(h) && h >= min && h <= max
      ? h
      : undefined;
  return { loop, fit, boxHeight };
}
